/**
 * Feedback React Query Hooks
 * Custom hooks for feedback data management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getEmployeeFeedback,
  createFeedback,
  deleteFeedback,
  type CreateFeedbackRequest,
} from '@/lib/api/feedback';

/**
 * Query keys for feedback
 */
export const feedbackKeys = {
  all: ['feedback'] as const,
  byEmployee: (employeeId: string) => [...feedbackKeys.all, 'employee', employeeId] as const,
};

/**
 * Fetch feedback for an employee
 */
export function useFeedback(employeeId: string) {
  return useQuery({
    queryKey: feedbackKeys.byEmployee(employeeId),
    queryFn: () => getEmployeeFeedback(employeeId),
  });
}

/**
 * Create feedback mutation
 */
export function useCreateFeedback(employeeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Omit<CreateFeedbackRequest, 'employeeId'>) =>
      createFeedback(employeeId, data),
    onSuccess: () => {
      // Invalidate feedback list to refetch
      queryClient.invalidateQueries({
        queryKey: feedbackKeys.byEmployee(employeeId),
      });
    },
  });
}

/**
 * Delete feedback mutation
 */
export function useDeleteFeedback(employeeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (feedbackId: string) => deleteFeedback(feedbackId),
    onSuccess: () => {
      // Invalidate feedback list to refetch
      queryClient.invalidateQueries({
        queryKey: feedbackKeys.byEmployee(employeeId),
      });
    },
  });
}
